import React, {
  ChangeEvent,
  FunctionComponent,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import { INPUT_TYPES } from '@savgroup-front-common/constants/shared';
import { MessageType } from '@savgroup-front-common/types';

import CheckboxNaked from '../../atoms/checkbox/CheckboxNaked';
import { getFinalFieldState } from '../../atoms/Form/common/helpers/getFinalFieldState';
import {
  FIELD_STATUS,
  FieldMessages,
} from '../../atoms/Form/common/helpers/getFinalFieldState.types';
import {
  safeFormattedIntlString,
  SafeFormattedMessage,
  SafeFormattedMessageWithoutSpread,
} from '../../formatters';

import {
  $ImageContainer,
  $PostTitle,
  $RadioCardBody,
  $RadioCardContainer,
  $RadioCardInfo,
  $RadioCardLabel,
  $RadioCardLabelContainer,
  $RadioCardTitle,
  $TextContainer,
} from './RadioCardColumn.styles';

export interface RadioCardColumnProps {
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  isChecked?: boolean;
  title?: string | MessageType;
  customTitle?: ReactElement;
  image: ReactNode;
  name: string;
  label?: MessageType;
  didactics?: string | MessageType;
  dataTestId?: string;
  value?: string;
  children?: ReactNode;
  errors?: FieldMessages;
  warnings?: FieldMessages;
  successes?: FieldMessages;
  isError?: boolean;
  isSuccess?: boolean;
  isWarning?: boolean;
  postTitle?: ReactNode;
}

interface RadioCardWithRefProps extends RadioCardColumnProps {
  forwardedRef?: Ref<HTMLInputElement>;
}

const RadioCardColumn: FunctionComponent<
  React.PropsWithChildren<RadioCardWithRefProps>
> = ({
  isChecked = undefined,
  onChange = undefined,
  onClick = undefined,
  forwardedRef = null,
  title,
  customTitle,
  label,
  didactics = undefined,
  children = null,
  isError = false,
  isWarning = false,
  isSuccess = false,
  errors = {},
  warnings = {},
  successes = {},
  image,
  name,
  value,
  dataTestId,
  postTitle,
}) => {
  const [status] = getFinalFieldState({
    errors: { isStatus: isError, messages: errors },
    warnings: { isStatus: isWarning, messages: warnings },
    successes: { isStatus: isSuccess, messages: successes },
    name,
  });

  return (
    <$RadioCardLabelContainer
      $status={status}
      onClick={onClick}
      aria-label={safeFormattedIntlString((title ?? label ?? didactics) as any)}
    >
      <CheckboxNaked
        checked={isChecked}
        onChange={onChange}
        ref={forwardedRef}
        name={name}
        danger={status === FIELD_STATUS.ERROR}
        value={value}
        type={INPUT_TYPES.RADIO}
        dataTestId={dataTestId}
        isHidden
      />
      <$RadioCardContainer $status={status} data-testid="RadioCardContainer">
        <$RadioCardInfo>
          <$ImageContainer $status={status}>{image}</$ImageContainer>

          <$TextContainer
            title={label ? safeFormattedIntlString(label) : undefined}
          >
            {title && (
              <$RadioCardTitle data-testid="RadioCardTitle">
                <SafeFormattedMessageWithoutSpread message={title} />
              </$RadioCardTitle>
            )}
            {customTitle && (
              <$RadioCardTitle data-testid="RadioCardTitle">
                {customTitle}
              </$RadioCardTitle>
            )}
            {label && (
              <$RadioCardLabel data-testid="RadioCardLabel">
                {SafeFormattedMessage(label)}
              </$RadioCardLabel>
            )}
            {didactics && (
              <$RadioCardLabel data-testid="RadioCardLabel">
                {SafeFormattedMessage(didactics)}
              </$RadioCardLabel>
            )}
            {postTitle && <$PostTitle>{postTitle}</$PostTitle>}
          </$TextContainer>
        </$RadioCardInfo>
        {children && <$RadioCardBody>{children}</$RadioCardBody>}
      </$RadioCardContainer>
    </$RadioCardLabelContainer>
  );
};

export default React.forwardRef<HTMLInputElement, RadioCardColumnProps>(
  (props, ref) => <RadioCardColumn forwardedRef={ref} {...props} />,
);
